import type { AddressInfo } from '../types/blockchain.types';
import { generateAddressPDF } from '../services/pdfGenerator';

interface QuickViewProps {
  addressInfo: AddressInfo;
}

export function QuickView({ addressInfo }: QuickViewProps) {
  const handleDownloadPDF = () => {
    generateAddressPDF(addressInfo);
  };
  const formatDate = (timestamp?: number) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-white dark:bg-legal-800 border border-gray-200 dark:border-legal-700 rounded-lg p-6 shadow-sm">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{addressInfo.network.name}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {addressInfo.network.explorer_name} Explorer
          </p>
        </div>
        <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full">
          {addressInfo.network.category.toUpperCase()}
        </span>
      </div>

      <div className="space-y-4">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Address</p>
          <p className="font-mono text-sm break-all text-gray-900 dark:text-white">{addressInfo.address}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Balance</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">{addressInfo.balance}</p>
          </div>

          {addressInfo.tx_count !== undefined && (
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Transactions</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{addressInfo.tx_count}</p>
            </div>
          )}
        </div>

        {(addressInfo.first_tx_timestamp || addressInfo.last_tx_timestamp) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-legal-700">
            {addressInfo.first_tx_timestamp && (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">First Activity</p>
                <p className="text-sm text-gray-900 dark:text-white">{formatDate(addressInfo.first_tx_timestamp)}</p>
              </div>
            )}

            {addressInfo.last_tx_timestamp && (
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Last Activity</p>
                <p className="text-sm text-gray-900 dark:text-white">{formatDate(addressInfo.last_tx_timestamp)}</p>
              </div>
            )}
          </div>
        )}

        {addressInfo.is_contract && (
          <div className="pt-4 border-t border-gray-200 dark:border-legal-700">
            <span className="inline-flex items-center px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 text-xs font-medium rounded-full">
              Smart Contract
            </span>
          </div>
        )}

        <div className="pt-4 border-t border-gray-200 dark:border-legal-700">
          <button
            onClick={handleDownloadPDF}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-medium rounded-lg transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Download PDF Constancia
          </button>
        </div>
      </div>
    </div>
  );
}
